import { useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { ApiError, api } from "../api/client";
import { Button, Card, EmptyState, PageHeader, Skeleton } from "../components/ui";
import { formatMoney } from "../lib/format";
import type { CreditNoteItemInput } from "../api/client";

const REASONS = [
  ["billing_adjustment", "Billing adjustment"],
  ["service_not_rendered", "Service not rendered"],
  ["cancelled_booking", "Cancelled booking"],
  ["duplicate", "Duplicate"],
  ["quality_issue", "Quality issue"],
  ["goodwill", "Goodwill"],
  ["other", "Other"],
] as const;

/**
 * An issued invoice can be credited whatever its payment state -- crediting a
 * paid invoice is legitimate and simply leaves a refund owed. Only a draft (not
 * yet issued) or a void one is excluded.
 */
const CREDITABLE = new Set(["sent", "part_paid", "paid", "overdue"]);

/**
 * Issue a credit note against an issued invoice.
 *
 * The invoice lines are loaded and their quantities default to the full line, so
 * a full credit is one click and a partial credit is "type a smaller number".
 * The server recomputes the tax-aware total; the client only supplies inputs.
 */
export function CreditNoteFormPage() {
  const navigate = useNavigate();
  // An invoice can arrive preselected from its own detail screen
  // (`/billing/credit-notes/new?invoice=<id>`), so "issue a credit note" is one
  // step from the invoice it corrects rather than a re-selection.
  const [params] = useSearchParams();
  const invoices = useQuery({
    queryKey: ["invoices", "creditable"],
    queryFn: ({ signal }) => api.invoices({ page_size: 200 }, signal),
    select: (page) => ({
      ...page,
      results: page.results.filter((row) => CREDITABLE.has(row.status)),
    }),
  });

  const [invoiceId, setInvoiceId] = useState(params.get("invoice") ?? "");
  const [reason, setReason] = useState("billing_adjustment");
  const [details, setDetails] = useState("");
  const inFlight = useRef(false);

  const invoice = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: ({ signal }) => api.invoice(invoiceId, signal),
    enabled: Boolean(invoiceId),
  });

  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const items = invoice.data?.items ?? [];

  function buildLines(): CreditNoteItemInput[] {
    return items.map((item, index) => ({
      invoice_item: item.id,
      quantity: quantities[item.id] ?? item.quantity,
      sort_order: index,
    }));
  }

  // The preview is computed by the server: the tax-aware total on screen is the
  // same arithmetic that will be stored, and an over-credit comes back as a 400
  // rather than being silently allowed by a parallel client-side calculation.
  const preview = useQuery({
    queryKey: ["credit-note-preview", invoiceId, quantities],
    queryFn: () =>
      api.previewCreditNote({ invoice: invoiceId, reason, details, items: buildLines() }),
    enabled: Boolean(invoiceId) && items.length > 0,
    retry: false,
  });

  const create = useMutation({
    mutationFn: () =>
      api.createCreditNote({ invoice: invoiceId, reason, details, items: buildLines() }),
    onSuccess: (note) => navigate(`/billing/credit-notes/${note.id}`),
  });

  const valid = Boolean(invoiceId) && items.length > 0 && preview.isSuccess && !preview.isError;

  return (
    <>
      <BackLink />
      <PageHeader title="New credit note" subtitle="A correction to an issued invoice." />

      <form
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (!valid || inFlight.current) return;
          inFlight.current = true;
          create.mutate(undefined, {
            onSettled: () => {
              inFlight.current = false;
            },
          });
        }}
        className="space-y-6"
      >
        <Card>
          <h2 className="mb-4 text-sm font-semibold">Invoice</h2>
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
              Credit against
            </span>
            <select
              value={invoiceId}
              onChange={(e) => {
                setInvoiceId(e.target.value);
                setQuantities({});
              }}
              className={INPUT}
            >
              <option value="">Select an issued invoice…</option>
              {invoices.data?.results.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.number || "Draft"} · {row.contact_name || "—"} ·{" "}
                  {formatMoney(row.total_amount)}
                </option>
              ))}
            </select>
          </label>
        </Card>

        {invoiceId ? (
          <Card>
            <h2 className="mb-4 text-sm font-semibold">Lines to credit</h2>
            {invoice.isPending ? (
              <Skeleton className="h-24 w-full" />
            ) : invoice.isError ? (
              <EmptyState title="Could not load that invoice" body="Please try another." />
            ) : items.length === 0 ? (
              <EmptyState title="No line items" body="This invoice has nothing to credit." />
            ) : (
              <div className="space-y-3">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="grid items-end gap-3 rounded-md border border-slate-200 p-3 sm:grid-cols-12"
                  >
                    <div className="sm:col-span-6">
                      <p className="text-sm text-slate-800">{item.description || "—"}</p>
                      <p className="text-xs text-slate-500">
                        {formatMoney(item.unit_price)} × {item.quantity} · tax{" "}
                        {formatMoney(item.tax_amount)}
                      </p>
                    </div>
                    <div className="sm:col-span-3">
                      <label className="block">
                        <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                          Credit qty
                        </span>
                        <input
                          inputMode="decimal"
                          value={quantities[item.id] ?? item.quantity}
                          onChange={(e) =>
                            setQuantities((prev) => ({ ...prev, [item.id]: e.target.value }))
                          }
                          className={INPUT}
                        />
                      </label>
                    </div>
                    <div className="text-right text-sm tabular-nums text-slate-600 sm:col-span-3">
                      of {item.quantity}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ) : null}

        <Card>
          <h2 className="mb-4 text-sm font-semibold">Reason</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                Reason
              </span>
              <select value={reason} onChange={(e) => setReason(e.target.value)} className={INPUT}>
                {REASONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                Details
              </span>
              <input value={details} onChange={(e) => setDetails(e.target.value)} className={INPUT} />
            </label>
          </div>
        </Card>

        {invoiceId && items.length > 0 ? (
          <Card>
            <h2 className="mb-4 text-sm font-semibold">Preview</h2>
            {preview.isPending ? (
              <Skeleton className="h-16 w-full" />
            ) : preview.isError ? (
              <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
                {preview.error instanceof ApiError
                  ? preview.error.message
                  : "This credit cannot be worked out."}
              </p>
            ) : preview.data ? (
              <>
                <dl className="space-y-3 text-sm">
                  <PreviewRow label="Subtotal" value={formatMoney(preview.data.subtotal)} />
                  <PreviewRow
                    label="Discount"
                    value={formatMoney(preview.data.discount_amount)}
                  />
                  <PreviewRow label="Tax" value={formatMoney(preview.data.tax_amount)} />
                  <div className="border-t border-slate-100 pt-3">
                    <PreviewRow
                      label="Total credit"
                      value={formatMoney(preview.data.total_amount)}
                      strong
                    />
                  </div>
                </dl>
                <p className="mt-3 text-xs text-slate-500">
                  {formatMoney(preview.data.remaining)} left to credit on this invoice before this
                  note.
                </p>
              </>
            ) : null}
          </Card>
        ) : null}

        {create.isError ? (
          <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {create.error instanceof ApiError
              ? create.error.message
              : "Could not issue the credit note."}
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          <Button type="submit" variant="primary" disabled={create.isPending || !valid}>
            {create.isPending ? "Issuing…" : "Issue credit note"}
          </Button>
          <Button onClick={() => navigate("/billing/credit-notes")}>Cancel</Button>
        </div>
      </form>
    </>
  );
}

const INPUT =
  "w-full rounded-md border-0 bg-white px-2.5 py-1.5 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900";

function PreviewRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className={strong ? "font-semibold text-slate-900" : "text-slate-800"}>{value}</dd>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/billing/credit-notes"
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
    >
      <ArrowLeft size={14} /> All credit notes
    </Link>
  );
}
